import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  BookOpen,
  Sparkles,
  Globe,
  Camera,
  Mail,
  Mic,
  PlayCircle,
  Target,
  Link as LinkIcon,
  ArrowUpRight,
} from 'lucide-react';
import { logger } from '../lib/logger';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const TEAL = '#004C54';
const TEAL_DARK = '#003a40';
const CYAN = '#4FBCC7';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_apex-athletes-mind/artifacts/ewmkb3wq_IMG_2112.JPG';

// Map admin-configurable icon keys to lucide-react components. Falls back to
// a generic link icon for unknown values so the admin can’t accidentally
// break the page by entering a typo.
const ICONS = {
  calendar: Calendar,
  book: BookOpen,
  sparkles: Sparkles,
  globe: Globe,
  instagram: Camera,
  mail: Mail,
  mic: Mic,
  youtube: PlayCircle,
  target: Target,
  link: LinkIcon,
};

function isExternal(url) {
  if (!url) return false;
  return /^https?:\/\//i.test(url);
}

function LinkCard({ link }) {
  const Icon = ICONS[link.icon] || LinkIcon;
  const external = isExternal(link.url);

  // Track the click, then navigate. We use sendBeacon-style fire-and-forget
  // so the user isn’t blocked waiting on the network round-trip.
  const trackAndGo = (e) => {
    if (link.id) {
      try {
        fetch(`${BACKEND}/api/bio-hub/click/${link.id}`, {
          method: 'POST',
          keepalive: true,
        }).catch((err) => {
          // Click tracking is best-effort — never block the navigation.
          logger.warn('[BioHub] click tracking failed:', err?.message || err);
        });
      } catch (err) {
        // fetch() itself synchronously threw (very rare — only e.g. malformed URL).
        logger.warn('[BioHub] click tracking threw:', err?.message || err);
      }
    }
    // For internal links we let React Router handle navigation; for external
    // links the <a target="_blank"> default behaviour is fine and `e` is just
    // here so we can stop propagation if we ever need to.
    if (!external && link.url?.startsWith('/')) {
      e.preventDefault();
      window.location.href = link.url;
    }
  };

  const commonProps = {
    onClick: trackAndGo,
    className:
      'group block w-full rounded-2xl bg-white/[0.07] hover:bg-white/[0.13] active:scale-[0.985] border border-white/15 hover:border-white/30 backdrop-blur-sm transition-colors transition-transform duration-200 px-5 py-4 sm:px-6 sm:py-5 text-left',
    'data-testid': `biohub-link-${link.slug || link.id}`,
  };

  const content = (
    <div className="flex items-center gap-4">
      <div
        className="flex-shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center"
        style={{ background: CYAN, color: TEAL_DARK }}
        aria-hidden="true"
      >
        <Icon className="h-5 w-5 sm:h-[22px] sm:w-[22px]" strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-bold text-[15px] sm:text-[16px] text-white leading-tight truncate">
          {link.title}
        </div>
        {link.subtitle ? (
          <div className="text-[12.5px] sm:text-[13px] text-white/65 truncate mt-0.5">
            {link.subtitle}
          </div>
        ) : null}
      </div>
      <ArrowUpRight
        className="h-4 w-4 text-white/50 group-hover:text-white transition-colors flex-shrink-0"
        aria-hidden="true"
      />
    </div>
  );

  if (external) {
    return (
      <a
        href={link.url}
        target="_blank"
        rel="noopener noreferrer"
        {...commonProps}
      >
        {content}
      </a>
    );
  }
  return (
    <a href={link.url || '#'} {...commonProps}>
      {content}
    </a>
  );
}

function LinkSkeleton() {
  return (
    <div
      className="w-full rounded-2xl bg-white/[0.05] border border-white/10 px-5 py-4 sm:px-6 sm:py-5"
      data-testid="biohub-skeleton-row"
    >
      <div className="flex items-center gap-4">
        <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-white/10" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 w-2/3 bg-white/15 rounded" />
          <div className="h-2.5 w-1/2 bg-white/10 rounded" />
        </div>
      </div>
    </div>
  );
}

export default function BioHub() {
  const [links, setLinks] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${BACKEND}/api/bio-hub/links`);
        if (!r.ok) throw new Error(`status ${r.status}`);
        const body = await r.json();
        if (!cancelled) setLinks(Array.isArray(body?.links) ? body.links : []);
      } catch (e) {
        if (!cancelled) {
          setLinks([]);
          setErr('Could not load links right now.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="min-h-screen text-white"
      style={{
        background: `linear-gradient(160deg, ${TEAL} 0%, ${TEAL_DARK} 60%, #00272b 100%)`,
      }}
      data-testid="biohub-page"
    >
      {/* Decorative ambient glows (kept under 20% viewport per design system) */}
      <div
        className="pointer-events-none absolute top-[-80px] right-[-60px] w-[260px] h-[260px] rounded-full opacity-[0.18] blur-3xl"
        style={{ background: CYAN }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute bottom-0 left-[-80px] w-[220px] h-[220px] rounded-full opacity-[0.10] blur-3xl"
        style={{ background: CYAN }}
        aria-hidden="true"
      />

      <div className="relative max-w-[460px] mx-auto px-5 sm:px-6 pt-12 pb-16 sm:pt-16">
        {/* Profile header */}
        <header className="flex flex-col items-center text-center" data-testid="biohub-header">
          <div
            className="w-[88px] h-[88px] rounded-full overflow-hidden ring-2 ring-white/20 shadow-[0_8px_28px_rgba(0,0,0,0.35)] bg-white/10"
            data-testid="biohub-avatar"
          >
            <img
              src={LOGO_URL}
              alt="Apex Athletic Mind logo"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <h1
            className="font-heading text-[22px] sm:text-[26px] font-bold tracking-tight mt-4"
            data-testid="biohub-name"
          >
            Apex Athletic Mind
          </h1>
          <p
            className="font-mono-accent uppercase tracking-[0.22em] text-[10.5px] font-semibold mt-1.5"
            style={{ color: CYAN }}
            data-testid="biohub-tagline"
          >
            Mental performance · Coach Varsay Lewis, M.Ed.
          </p>
          <p className="text-[13.5px] text-white/70 leading-relaxed mt-3 max-w-[340px]">
            Tools, talks, and a free playbook for athletes, parents, and coaches.
          </p>
        </header>

        {/* Links list */}
        <div className="mt-9 space-y-3.5" data-testid="biohub-links">
          {links === null ? (
            <>
              <LinkSkeleton />
              <LinkSkeleton />
              <LinkSkeleton />
              <LinkSkeleton />
            </>
          ) : links.length === 0 ? (
            <div
              className="text-center text-white/60 text-[13.5px] py-8"
              data-testid="biohub-empty"
            >
              {err || 'No links yet. Check back soon.'}
            </div>
          ) : (
            links.map((l) => <LinkCard key={l.id} link={l} />)
          )}
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center text-[11.5px] text-white/45">
          <Link
            to="/"
            className="hover:text-white/80 transition-colors"
            data-testid="biohub-footer-home"
          >
            apexathleticmind.com
          </Link>
          <span className="mx-2">·</span>
          <span>© {new Date().getFullYear()} Apex Athletic Mind</span>
        </footer>
      </div>
    </div>
  );
}

export { BioHub };
